// @flow
import * as Immutable from 'immutable';
import ImmutableModel from './ImmutableModel';

import { PhoneNumber } from './PhoneNumber';
import { TeamId } from './TeamId';
import { EmailAddress } from './EmailAddress';
import { AuditMetadata } from './AuditMetadata';

import type { PhoneNumberModelType } from 'common/models/flow/PhoneNumber';
import type { TeamIdModelType } from 'common/models/flow/TeamId';
import type { EmailAddressModelType } from 'common/models/flow/EmailAddress';
import type { AuditMetadataModelType } from 'common/models/flow/AuditMetadata';

type StatusEnum = 'ACTIVE' | 'INACTIVE';
export type ContactTypeEnum =
    | 'CUSTOMER'
    | 'CONSIGNEE'
    | 'SALES_REPRESENTATIVE'
    | 'WAREHOUSE_MANAGER'
    | 'DISTRIBUTOR'
    | 'TRANSPORTER'
    | 'SUPPLIER'
    | 'RETAILER';

export type ContactModelType = {
    clientId: string,
    id: string,
    code: string,
    name: string,
    status: StatusEnum,
    phoneNumber: PhoneNumberModelType,
    landlineNumber: ?PhoneNumberModelType,
    type: ContactTypeEnum,
    teams: Array<TeamIdModelType>,
    emailAddress: ?EmailAddressModelType,
    company: ?string,
    additionalPhoneNumbers: Array<PhoneNumberModelType>,
    additionalEmailAddresses: Array<EmailAddressModelType>,
    auditMetadata: ?AuditMetadataModelType,
    customProperties: { [key: string]: string },
    version: number
};

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class Contact extends ImmutableModel {
    static fromJS(json: ContactModelType): Contact {
        const state: Object = Object.assign({}, json);
        state.phoneNumber = PhoneNumber.fromJS(state.phoneNumber);
        state.landlineNumber = state.landlineNumber
            ? PhoneNumber.fromJS(state.landlineNumber)
            : state.landlineNumber;
        state.teams = Immutable.List(state.teams).map(item =>
            TeamId.fromJS(item)
        );
        state.emailAddress = state.emailAddress
            ? EmailAddress.fromJS(state.emailAddress)
            : state.emailAddress;
        state.additionalPhoneNumbers = Immutable.List(
            state.additionalPhoneNumbers
        ).map(item => PhoneNumber.fromJS(item));
        state.additionalEmailAddresses = Immutable.List(
            state.additionalEmailAddresses
        ).map(item => EmailAddress.fromJS(item));
        state.auditMetadata = state.auditMetadata
            ? AuditMetadata.fromJS(state.auditMetadata)
            : state.auditMetadata;
        state.customProperties = Immutable.Map(state.customProperties);
        return new this(Immutable.Map(state));
    }

    toJS(): ContactModelType {
        return {
            clientId: this.clientId,
            id: this.id,
            code: this.code,
            name: this.name,
            status: this.status,
            phoneNumber: this.phoneNumber.toJS(),
            landlineNumber: this.landlineNumber
                ? this.landlineNumber.toJS()
                : this.landlineNumber,
            type: this.type,
            teams: this.teams.toArray().map(item => item.toJS()),
            emailAddress: this.emailAddress
                ? this.emailAddress.toJS()
                : this.emailAddress,
            company: this.company,
            additionalPhoneNumbers: this.additionalPhoneNumbers
                .toArray()
                .map(item => item.toJS()),
            additionalEmailAddresses: this.additionalEmailAddresses
                .toArray()
                .map(item => item.toJS()),
            auditMetadata: this.auditMetadata
                ? this.auditMetadata.toJS()
                : this.auditMetadata,
            customProperties: this.customProperties.toObject(),
            version: this.version
        };
    }

    get clientId(): string {
        return this._state.get('clientId');
    }

    setClientId(clientId: string): this {
        return this.clone(this._state.set('clientId', clientId));
    }

    get id(): string {
        return this._state.get('id');
    }

    setId(id: string): this {
        return this.clone(this._state.set('id', id));
    }

    get code(): string {
        return this._state.get('code');
    }

    setCode(code: string): this {
        return this.clone(this._state.set('code', code));
    }

    get name(): string {
        return this._state.get('name');
    }

    setName(name: string): this {
        return this.clone(this._state.set('name', name));
    }

    get status(): StatusEnum {
        return this._state.get('status');
    }

    setStatus(status: StatusEnum): this {
        return this.clone(this._state.set('status', status));
    }

    get phoneNumber(): PhoneNumber {
        return this._state.get('phoneNumber');
    }

    setPhoneNumber(phoneNumber: PhoneNumber): this {
        return this.clone(this._state.set('phoneNumber', phoneNumber));
    }

    get landlineNumber(): ?PhoneNumber {
        return this._state.get('landlineNumber');
    }

    setLandlineNumber(landlineNumber: ?PhoneNumber): this {
        return this.clone(this._state.set('landlineNumber', landlineNumber));
    }

    get type(): ContactTypeEnum {
        return this._state.get('type');
    }

    setType(type: ContactTypeEnum): this {
        return this.clone(this._state.set('type', type));
    }

    get teams(): Immutable.List<TeamId> {
        return this._state.get('teams');
    }

    setTeams(teams: Immutable.List<TeamId>): this {
        return this.clone(this._state.set('teams', teams));
    }

    get emailAddress(): ?EmailAddress {
        return this._state.get('emailAddress');
    }

    setEmailAddress(emailAddress: ?EmailAddress): this {
        return this.clone(this._state.set('emailAddress', emailAddress));
    }

    get company(): ?string {
        return this._state.get('company');
    }

    setCompany(company: ?string): this {
        return this.clone(this._state.set('company', company));
    }

    get additionalPhoneNumbers(): Immutable.List<PhoneNumber> {
        return this._state.get('additionalPhoneNumbers');
    }

    setAdditionalPhoneNumbers(
        additionalPhoneNumbers: Immutable.List<PhoneNumber>
    ): this {
        return this.clone(
            this._state.set('additionalPhoneNumbers', additionalPhoneNumbers)
        );
    }

    get additionalEmailAddresses(): Immutable.List<EmailAddress> {
        return this._state.get('additionalEmailAddresses');
    }

    setAdditionalEmailAddresses(
        additionalEmailAddresses: Immutable.List<EmailAddress>
    ): this {
        return this.clone(
            this._state.set(
                'additionalEmailAddresses',
                additionalEmailAddresses
            )
        );
    }

    get auditMetadata(): ?AuditMetadata {
        return this._state.get('auditMetadata');
    }

    setAuditMetadata(auditMetadata: ?AuditMetadata): this {
        return this.clone(this._state.set('auditMetadata', auditMetadata));
    }

    get customProperties(): Immutable.Map<string, string> {
        return this._state.get('customProperties');
    }

    setCustomProperties(customProperties: Immutable.Map<string, string>): this {
        return this.clone(
            this._state.set('customProperties', customProperties)
        );
    }

    get version(): number {
        return this._state.get('version');
    }

    setVersion(version: number): this {
        return this.clone(this._state.set('version', version));
    }
}
